import { type FC, useEffect, useMemo, useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import Spinner from "../../common/components/Spinner/Spinner";
import TVAccountSelectionResultsTable from "./TVAccountSelectionResultsTable";
import { type AccountInfoData, getAccountsData } from "./api/GetAccountList";
import { redirectToUrl } from "../../utils/url/urlUtils";
import { sendData } from "../../api/salesman/SetAccount";
import { invalidateNavigationCache } from "../../Header/redux/loadNavigationEpic";
import { useTranslation } from "react-i18next";
import AccountSelectionSearch from "../AccountSelectionView/components/AccountSelectionSearch";
import { Button } from "~/common/components/Button/Button";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { AccountSelectionBottomButton } from "../AccountSelectionPage";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from "classnames";
import { Alert } from "~/common/m3/Alert/Alert";

export interface TVAccountSelectionrPageFilterConfig {
  query: string;
  queryType: string;
}

const useStyles = makeStyles({
  button: { borderRadius: "100px !important", padding: "10px 100px !important" },
  marginTop: { marginTop: "32px !important" },

  overflow: { overflow: "auto" },
  sticky: { position: "sticky", left: "0" },
});

export const TVAccountSelectionPage: FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AccountInfoData[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountInfoData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [inputValue, setInputValue] = useState<string>("");

  const fetchData = async () => {
    try {
      const { rows, selectedAccountId } = await getAccountsData();
      const translatedRows = rows.map((row) => ({ ...row, country: t(row.country) }));
      setRows(translatedRows);
      setSelectedAccount(translatedRows.find((row) => row.id === selectedAccountId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [t]);

  const onRadioClickChangeActiveState = (item: AccountInfoData) => {
    setSelectedAccount(item);
  };

  const filteredRows = useMemo(
    () =>
      rows.filter(({ name, number, endCustomerName, endCustomerCode, region, country, endCustomerSector }) =>
        [name, number, endCustomerName, endCustomerCode, region, country, endCustomerSector].some((value) =>
          value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      ),
    [rows, inputValue],
  );

  const isDesktop = useIsMinDesktopSize();

  const buttonDisabled = filteredRows.findIndex((account) => account === selectedAccount) === -1 || !selectedAccount;

  const handleAccountChange = () =>
    setAccount(selectedAccount!.uid, selectedAccount!.salesOrg, selectedAccount!.endCustomerCode);

  const styles = useStyles();

  return (
    <>
      <Box className={styles.overflow}>
        <div className={styles.sticky}>
          <PageTitle title={t("accountSelectionPage.tableView.header")} />

          {loading && !selectedAccount && <Spinner className="mb-30" />}

          <div className="d-flex flex-justify-between">
            <AccountSelectionSearch value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
            {isDesktop && filteredRows.length !== 0 && (
              <Button
                className={classNames(styles.button, "mb-3")}
                onClick={handleAccountChange}
                disabled={buttonDisabled}
              >
                {t("accountSelection.buttonText")}
              </Button>
            )}
          </div>
        </div>

        {!loading && !error && selectedAccount && (
          <TVAccountSelectionResultsTable
            rows={filteredRows}
            selectedId={selectedAccount.id}
            onRadioClickChangeActiveState={onRadioClickChangeActiveState}
          />
        )}

        {!loading && error && <Alert severity="error">{t("page.accountSelectionPage.error")}</Alert>}
        {isDesktop && filteredRows.length !== 0 && (
          <Button
            className={classNames(styles.button, styles.marginTop)}
            onClick={handleAccountChange}
            disabled={buttonDisabled}
          >
            {t("accountSelection.buttonText")}
          </Button>
        )}
      </Box>
      {!isDesktop && <AccountSelectionBottomButton onClick={handleAccountChange} disabled={buttonDisabled} />}
    </>
  );
};

const setAccount = (accountId: string, salesOrg?: string, endCustomerCode?: string) =>
  sendData({ accountId, salesOrg, endCustomerCode })
    .then((response) => response.json())
    .then((body) => {
      window.sessionStorage.setItem("accountChanged", "true");
      invalidateNavigationCache();
      redirectToUrl(body);
    });
